import {ReductionsState} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/reductions_state/v1/reductions_state.types';
import {useCallback, useDeferredValue, useMemo} from 'react';
import {
  useSelectedDiscount,
  useSelectedDiscountCallback,
} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscount';
import {DiscountType} from '../../backend-services/Campaigns/enums/DiscountType';
import {useUnlockedCouponTokens} from '../Coupons/useUnlockedCouponTokens';

/**
 * `useReductionsStateRequestParameter` returns a deferred value, which allows refetching the requests whenever the selected discount changes without
 * blocking the current UI updates/animations.
 */
export const useReductionsStateRequestParameter = (): ReductionsState => {
  const selectedDiscount = useDeferredValue(useSelectedDiscount());
  const {unlockedCouponTokens} = useUnlockedCouponTokens();

  const token =
    selectedDiscount?.discount?.$case === DiscountType.personalDiscount
      ? selectedDiscount.discount.personalDiscount.token.token
      : null;

  return useMemo(() => {
    return {
      discount: token ? {token} : undefined,
      unlockedCouponTokens: unlockedCouponTokens
        ? {unlockedCouponTokens}
        : undefined,
    };
  }, [token, unlockedCouponTokens]);
};

export const useReductionsStateRequestParameterCallback =
  (): (() => Promise<ReductionsState>) => {
    const getSelectedDiscount = useSelectedDiscountCallback();
    const {unlockedCouponTokens} = useUnlockedCouponTokens();

    return useCallback(async () => {
      const selectedDiscount = await getSelectedDiscount();

      const token =
        selectedDiscount?.discount?.$case === DiscountType.personalDiscount
          ? selectedDiscount.discount.personalDiscount.token.token
          : null;

      return {
        discount: token ? {token} : undefined,
        unlockedCouponTokens: unlockedCouponTokens
          ? {unlockedCouponTokens}
          : undefined,
      };
    }, [getSelectedDiscount, unlockedCouponTokens]);
  };
